import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as RevenueManagementIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import { ReactComponent as TickIcon } from '../../../assets/icons/tick-circle-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import {
  FilterRevenueInterFace,
  ManagementStationInterFace,
} from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  fetchAllStations,
  updateSingleStation,
  setRevenueStationFilterData,
  updateRevenueManagementStationFilters,
  // fetchRevenueManagementStationFilters,
  disableRevenueManagementStationFilterApplied,
  setSortData,
} from '../../../redux/slices/RevenueManagementLocationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../shared-components/table-side-panel/LocationSidePanel';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween, removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueApi from '../../../utils/api/revenue';
import type { UtilityCostFormState } from '../index';

import AddAccountRevenueShare from './AddAccountRevenueShare';
import FilterRevenue from './filter-station-management';
import RevenueManagementToggle from './RevenueManageToggle';


interface ModalState {
  status: boolean;
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

interface ManagementStationRevenueProperties {
  currentPage: string;
  selectedAccountId: string | number;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
  utilityCost?: UtilityCostFormState;
}

interface EditPercentageProperties {
  id: string;
  value: number | string;
  accountCustomId: string;
  stationId: string;
}

/**
 * ManagementStationRevenue page function component
 * @param {any} properties - The properties
 * @returns {any} The rendered component
 */
function ManagementStationRevenue(properties: Readonly<ManagementStationRevenueProperties>) {
  const { t, i18n } = useTranslation('revenue');
  const { currentPage, setCurrentPage, selectedAccountId, utilityCost } = properties;
  const { portalType } = usePortal();
  const isPartnerPortal = portalType === PortalType.PARTNER;

  const dispatch = useAppDispatch();
  const inputReference = useRef<HTMLSpanElement>(null);
  const {
    stations,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filter,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueLocation);
  const [searchParameters, setSearchParameters] = useSearchParams();

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  const [editPercent, setEditPercent] = useState<EditPercentageProperties>({
    id: '',
    value: 0,
    accountCustomId: '',
    stationId: '',
  });

  const [modalView, setModalView] = useState<ModalState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  const [addRevenueShareOpen, setAddRevenueShareOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: true,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchStationsData = (data: any) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchAllStations(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchStationsData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page,
      limit,
      sortField,
      sortBy,
    });
  };

  /**
   * Function to close the add revenue share side panel, optionally showing a
   * success modal and refreshing the table when a save just completed
   * @param {string} [successMessage] - The success message to show, if the panel closed after a successful save
   */
  const handleCloseAddRevenueShare = (successMessage?: string) => {
    setAddRevenueShareOpen(false);
    if (successMessage) {
      handleRefresh();
      setModalView({
        status: true,
        title: '',
        header: false,
        rightCorner: '',
        notification: true,
        content: (
          <SuccessNotification
            title={successMessage}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={closeModal}
          />
        ),
      });
    }
  };

  /**
   * Function to handle edit percentage
   * @param {any} rowData - The rowData
   */
  const handleEditPercentage = (rowData: ManagementStationInterFace) => {
    setEditPercent({
      id: rowData.id,
      value: rowData.revenuePercentage ? rowData.revenuePercentage : 0,
      accountCustomId: rowData.accountCustomId ?? '',
      stationId: rowData.stationId ?? '',
    });
  };

  /**
   * API call to update percentage
   */
  const handleUpdateRevenuePercentage = async () => {
    const savedPercentage = editPercent.value === '' ? 0 : Number(editPercent.value);
    const cpoPercentage = Math.max(0, 100 - savedPercentage);
    await revenueApi
      .updateRevenueConfigurationPercentage({
        partnerRevenueSharePercentage: savedPercentage,
        cpoRevenueSharePercentage: cpoPercentage,
        accountCustomId: editPercent.accountCustomId,
        stationId: editPercent.stationId,
      })
      .then(() => {
        const updatedAccount: ManagementStationInterFace | undefined = stations.find(
          (station: any) => station.id === editPercent.id,
        );
        if (updatedAccount) {
          dispatch(
            updateSingleStation({
              ...updatedAccount,
              revenuePercentage: savedPercentage,
            }),
          );
        }
        setEditPercent({
          id: '',
          value: 0,
          accountCustomId: '',
          stationId: '',
        });
        setModalView({
          status: true,
          title: '',
          header: false,
          rightCorner: '',
          notification: true,
          content: (
            <SuccessNotification
              title={t('stationRevenueUpdated')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={closeModal}
            />
          ),
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to discard the in-progress percentage edit without saving
   */
  const handleCancelEditPercentage = () => {
    setEditPercent({
      id: '',
      value: 0,
      accountCustomId: '',
      stationId: '',
    });
  };

  /**
   * Function to discard the in-progress edit when clicking outside the input field
   * @param {MouseEvent} event - The mouse event
   */
  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as Node;
    const clickedActionButtons = (target as HTMLElement).closest?.('.action-button-groups');
    if (
      editPercent.id !== '' &&
      inputReference.current &&
      !inputReference.current.contains(target) &&
      !clickedActionButtons
    ) {
      handleCancelEditPercentage();
    }
  };

  /**
   * Function to handle percentage edit, clamping to 0-100 while preserving decimals.
   * Keeps the field empty while the user is actively clearing it instead of
   * snapping back to 0, so typing a fresh value doesn't concatenate onto a stale "0".
   * @param {string} value - The percentage value
   */
  const handleChangePercentage = (value: string) => {
    if (value === '') {
      setEditPercent({
        ...editPercent,
        value: '',
      });
      return;
    }
    const percentage = Math.min(100, Math.max(0, Number(value) || 0));
    setEditPercent({
      ...editPercent,
      value: percentage,
    });
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView: sidePanelModalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleDeleteStationClick,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: stations,
  });
  const column = [
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof ManagementStationInterFace,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onDeleteStation={handleDeleteStationClick}
          onSuccess={() => handleEditModeChange(false)}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof ManagementStationInterFace,
      sortAllow: true,
      ...(!isPartnerPortal
        ? {
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => handleEditModeChange(false)}
              />
            ),
            disableNewTab: true,
          }
        : {}),
    },
    {
      label: t('stationRevenuePercentage'),
      accessorKey: 'partnerRevenuePercentage' as keyof ManagementStationInterFace,
      sortAllow: true,
      /**
       * Renders the editable station revenue percentage cell
       * @param {ManagementStationInterFace} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementStationInterFace) => (
        <div className="table-row-data">
          {editPercent.id === rowData.id ? (
            <span
              role="presentation"
              className="table-percentage-input"
              ref={inputReference}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleUpdateRevenuePercentage();
                } else if (event.key === 'Escape') {
                  handleCancelEditPercentage();
                }
              }}>
              <CustomInput
                type="number"
                name="revenuePercentage"
                placeholder="0"
                value={editPercent.value}
                onChangeValue={(_name, inputValue) => handleChangePercentage(String(inputValue))}
                maxValue={100}
                decimalLimit={2}
                autoFocus
                fontSize="small"
              />
            </span>
          ) : (
            <span>{Number(rowData.partnerRevenuePercentage ?? 0).toFixed(2)}</span>
          )}
        </div>
      ),
    },
    {
      label: t('cpoRevenuePercentage'),
      accessorKey: 'cpoRevenuePercentage' as keyof ManagementStationInterFace,
      sortAllow: true,
      /**
       * Renders the read-only CPO revenue percentage cell as the balance of the station share
       * @param {ManagementStationInterFace} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementStationInterFace) => {
        const stationPercentage =
          editPercent.id === rowData.id ? editPercent.value : (rowData.revenuePercentage ?? 0);
        const cpoPercentage =
          editPercent.id === rowData.id
            ? Math.max(0, 100 - Number(stationPercentage))
            : (rowData.cpoRevenuePercentage ?? 0);

        return (
          <div className="table-row-data">
            <span>{Number(cpoPercentage).toFixed(2)}</span>
          </div>
        );
      },
    },
    {
      label: t('action'),
      accessorKey: '' as keyof ManagementStationInterFace,
      sortAllow: false,
      columnWidth: 60,
      sticky: 'right' as const,
      /**
       * Function for action buttons
       * @param {any} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementStationInterFace) =>
        editPercent.id === rowData.id ? (
          <div className="action-button-groups">
            <div
              className="action-button-items view-color"
              onClick={() => handleUpdateRevenuePercentage()}
              role="presentation">
              <TickIcon />
            </div>
            <div
              className="action-button-items delete-color"
              onClick={() => handleCancelEditPercentage()}
              role="presentation">
              <CloseIcon />
            </div>
          </div>
        ) : (
          <div className="action-button-groups">
            <div
              className="action-button-items view-color"
              onClick={() => handleEditPercentage(rowData)}
              role="presentation">
              <EditIcon />
            </div>
          </div>
        ),
    },
  ];

  /**
   * Handle submit filter function
   * @param {any} filterData - The filter data
   */
  const handleSubmitFilter = (filterData: FilterRevenueInterFace) => {
    dispatch(setRevenueStationFilterData(filterData));
    fetchStationsData({
      ...filterData,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiParameters.page,
      limit,
    });
    setModalView((previous) => ({
      ...previous,
      status: false,
    }));
  };

  /**
   * Filter clear
   */
  const clearFilter = () => {
    dispatch(setRevenueStationFilterData({}));
    fetchStationsData({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiParameters.page,
      limit,
    });
  };

  /**
   * User filter function
   */
  const filterFunction = () => {
    setModalView({
      status: true,
      title: t('rolesFilter'),
      header: false,
      content: (
        <FilterRevenue
          closeModal={closeModal}
          handleSubmitFilter={handleSubmitFilter}
          oldValue={filter}
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo', 'S.No'), accessorKey: '__serial__' },
    { label: t('stationName', 'Station Name'), accessorKey: 'chargingStationName' },
    { label: t('accountID', 'Account ID'), accessorKey: 'accountCustomId' },
    {
      label: t('stationRevenuePercentage', 'Station Revenue %'),
      accessorKey: 'partnerRevenuePercentage',
    },
    { label: t('cpoRevenuePercentage', 'CPO Revenue %'), accessorKey: 'cpoRevenuePercentage' },
    { label: t('createdAt', 'Created At'), accessorKey: 'createdAt' },
    { label: t('updatedAt', 'Updated At'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Table pagination function
   * @param {any} value - The page props
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchStationsData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...value,
      sortField,
      sortBy,
    });
  };

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = removeEmptyKeys({
      type: 'Station',
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      selectedAccountId,
      sortBy,
      sortField,
    });

    revenueApi
      .fetchRevenueConfigurationListExport(removeEmptyKeys(payload))
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: ManagementStationInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'chargingStationName':
                return row.chargingStationName || 'N/A';

              case 'accountCustomId':
                return row.accountCustomId || 'N/A';

              case 'partnerRevenuePercentage':
                return `${ToFixedValue(row.revenuePercentage ?? row.partnerRevenuePercentage, 2)}`;

              case 'cpoRevenuePercentage': {
                const partnerPercentage =
                  row.revenuePercentage ?? row.partnerRevenuePercentage ?? 0;
                const cpoPercentage = Math.max(0, 100 - Number(partnerPercentage));
                return `${ToFixedValue(cpoPercentage, 2)}`;
              }

              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt || '');

              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt || '');

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-management-station-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-management-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 404,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchStationsData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [editPercent]);

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchStationsData({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
      });
      return;
    }

    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchStationsData({
      page: DefaultApiLimit.page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
    });

    if (isFilterUpdated) {
      dispatch(disableRevenueManagementStationFilterApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    if (editPercent.id && inputReference.current) {
      inputReference.current.focus(); // Ensure the input is focused after state update
    }
  }, [editPercent.id]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('revenueManagement'),
        icon: <RevenueManagementIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  useEffect(() => {
    if (searchParameters.get('stationId') || searchParameters.get('accountCustomId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('accountCustomId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <ModalBox
        open={openStatus}
        title={sidePanelModalView.title}
        content={sidePanelModalView.content}
        closeModal={() => setOpenStatus(false)}
        header={sidePanelModalView.header}
        rightCorner={sidePanelModalView.rightCorner}
      />
      <SidePaneModel
        open={addRevenueShareOpen}
        title=""
        hideHeader
        closePanel={() => setAddRevenueShareOpen(false)}
        content={
          <AddAccountRevenueShare
            closeModal={handleCloseAddRevenueShare}
            revenueType="station"
            open={addRevenueShareOpen}
            utilityCost={utilityCost}
          />
        }
      />
      <CustomTable
        header={column}
        data={stations}
        serialNo
        created
        lastUpdated
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateRevenueManagementStationFilters}
        filterListLoading={filterListLoading}
        // filterListToDispatch={fetchRevenueManagementStationFilters}
        filterClearFunction={clearFilter}
        filterFunction={filterFunction}
        sizeChangeButtonRequired
        rightTopContent={
          <div className="table-bottom-button-list">
            {renderDateFilters()}
            <div className="right-action">
              <RevenueManagementToggle currentPage={currentPage} setCurrentPage={setCurrentPage} />
              <MyButton
                svgIcon={<PlusIcon />}
                label={t('revenueShare')}
                buttonType="primary"
                onClickFunc={() => setAddRevenueShareOpen(true)}
              />
            </div>
          </div>
        }
        onClearFilterChange={() => {
          clearDateRange();
        }}
        tableHeaderVersion="v1"
        refreshRequired
        handleRefresh={handleRefresh}
        exportDataPdfOrCSV={permissionCheck('cpoRevenueExport') ? exportDataPdfOrCSV : undefined}
        exportLoader={exportLoader}
        onHiddenColumnsChange={setHiddenColumns}
        refershCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
      />
    </div>
  );
}

export default ManagementStationRevenue;
